/* Meat.Kitchen — concept 12 behaviours
   - header height → CSS variable (hero overlaps the translucent header)
   - rotating hero quotes + announcement messages
   - hero video play/pause + reduced-motion guard
   - mobile sticky CTA (appears after the hero scrolls away)
   - reviews rotation (auto-advances when there are more than three)
   - dinner-ideas horizontal track (more than three ideas)
   - Build-your-menu: quantity steppers, frequency (selling plan) sync, live summary
   - order minimum: soft gate on the cart drawer / cart page checkout button
   - catalog links (/products, /collections) → Build-your-menu page
   Settings arrive via window.MK (snippets/mk-tokens.liquid). Re-initialises on Theme Editor events. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var timers = new WeakMap();
  var MK = window.MK || {};
  var designMode = !!(MK.designMode || (window.Shopify && window.Shopify.designMode));

  function clearTimer(el) {
    var id = timers.get(el);
    if (id) {
      window.clearInterval(id);
      timers.delete(el);
    }
  }

  /* ---------- header height ---------- */
  function syncHeaderHeight() {
    var header = document.querySelector('.mk-header');
    if (!header) return;
    var h = Math.round(header.getBoundingClientRect().height);
    if (h > 0) document.documentElement.style.setProperty('--mk-header-h', h + 'px');
  }

  /* ---------- rotating stacks (hero quotes, announcements) ---------- */
  function initRotator(root) {
    clearTimer(root);
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-mk-rotate-item]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-mk-rotate-dot]'));
    if (!items.length) return;
    var index = Math.max(0, items.findIndex(function (el) { return el.classList.contains('is-active'); }));
    var interval = parseInt(root.getAttribute('data-interval'), 10) || 5200;

    function show(i) {
      items.forEach(function (el, n) { el.classList.toggle('is-active', n === i); });
      dots.forEach(function (el, n) { el.classList.toggle('is-active', n === i); });
    }
    show(index);
    if (items.length < 2 || reduceMotion.matches || interval <= 0) return;

    var paused = false;
    root.addEventListener('mouseenter', function () { paused = true; });
    root.addEventListener('mouseleave', function () { paused = false; });
    root.addEventListener('focusin', function () { paused = true; });
    root.addEventListener('focusout', function () { paused = false; });

    timers.set(root, window.setInterval(function () {
      if (paused || document.hidden) return;
      index = (index + 1) % items.length;
      show(index);
    }, interval));
  }

  /* ---------- hero video ---------- */
  function initHeroVideo(hero) {
    var video = hero.querySelector('video');
    var toggle = hero.querySelector('[data-mk-video-toggle]');
    if (!video) {
      if (toggle) toggle.hidden = true;
      return;
    }
    video.muted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');

    function setState(playing) {
      if (!toggle) return;
      toggle.setAttribute('data-state', playing ? 'playing' : 'paused');
      toggle.setAttribute('aria-label', playing ? toggle.getAttribute('data-label-pause') : toggle.getAttribute('data-label-play'));
    }

    if (reduceMotion.matches) {
      video.removeAttribute('autoplay');
      video.pause();
      setState(false);
    } else {
      var p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () { setState(false); });
      setState(true);
    }

    video.addEventListener('play', function () { setState(true); });
    video.addEventListener('pause', function () { setState(false); });

    if (toggle) {
      toggle.addEventListener('click', function () {
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      });
    }
  }

  /* ---------- mobile CTA ---------- */
  var ctaObserver = null;
  var ctaZoneObserver = null;
  function initMobileCta() {
    var cta = document.querySelector('.mk-mobile-cta');
    if (ctaObserver) {
      ctaObserver.disconnect();
      ctaObserver = null;
    }
    if (ctaZoneObserver) {
      ctaZoneObserver.disconnect();
      ctaZoneObserver = null;
    }
    document.body.classList.remove('mk-has-mobile-cta');
    if (!cta) return;
    var sentinel = document.querySelector('.mk-hero-actions');
    if (sentinel && !sentinel.getClientRects().length) sentinel = null; /* hero buttons hidden in the editor */
    sentinel = sentinel || document.querySelector('.mk-hero');
    if (!sentinel || !('IntersectionObserver' in window)) {
      cta.classList.add('is-visible');
      return;
    }
    /* Shows once the hero buttons scroll away; steps aside wherever the page already carries the action
       (the closing call-to-action and the footer), so it never covers a button or the legal row.
       Add data-mk-cta-hide to any other section that should hide it. */
    var zones = Array.prototype.slice.call(document.querySelectorAll('.mk-cta-wrap, .mk-footer, [data-mk-cta-hide]'));
    var pastHero = false;
    var inZone = [];
    function applyCta() {
      var show = pastHero && inZone.length === 0;
      cta.classList.toggle('is-visible', show);
      document.body.classList.toggle('mk-has-mobile-cta', show);
    }
    /* The hero observer's root reaches far below the screen, so "not intersecting" can only mean the hero
       buttons are above the top of the screen. That stays true after a jump straight past them (a menu
       anchor, a fast fling, a landscape phone where the buttons start below the fold). */
    ctaObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        pastHero = !entry.isIntersecting;
      });
      applyCta();
    }, { threshold: 0, rootMargin: '0px 0px 100000px 0px' });
    ctaObserver.observe(sentinel);
    ctaZoneObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var i = inZone.indexOf(entry.target);
        if (entry.isIntersecting && i === -1) inZone.push(entry.target);
        if (!entry.isIntersecting && i !== -1) inZone.splice(i, 1);
      });
      applyCta();
    }, { threshold: 0 });
    zones.forEach(function (zone) {
      ctaZoneObserver.observe(zone);
    });
  }


  /* ---------- reviews: rotating roles (1 feature + 2 cards) ---------- */
  function initReviews(grid) {
    clearTimer(grid);
    var items = Array.prototype.slice.call(grid.querySelectorAll('[data-mk-review]'));
    var n = items.length;
    if (!n) return;
    var section = grid.closest('.mk-section') || grid.parentElement;
    var dots = Array.prototype.slice.call(section.querySelectorAll('[data-mk-reviews-dot]'));
    var prev = section.querySelector('[data-mk-reviews-prev]');
    var next = section.querySelector('[data-mk-reviews-next]');
    var start = Math.max(0, items.findIndex(function (el) { return el.classList.contains('mk-review--feature'); }));
    var interval = parseInt(grid.getAttribute('data-interval'), 10) || 7000;
    var autoplay = grid.getAttribute('data-rotate') === 'true' && n > 3 && !reduceMotion.matches;
    var paused = false;

    function apply(animate) {
      items.forEach(function (el, i) {
        var offset = (i - start + n) % n;
        var role = offset === 0 ? 'mk-review--feature' : (offset < 3 ? 'mk-review--card' : 'mk-review--hidden');
        var was = el.classList.contains(role);
        el.classList.remove('mk-review--feature', 'mk-review--card', 'mk-review--hidden', 'is-entering');
        el.classList.add(role);
        el.style.order = offset;
        if (animate && !was && role !== 'mk-review--hidden' && !reduceMotion.matches) {
          void el.offsetWidth;
          el.classList.add('is-entering');
        }
      });
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === start);
        d.setAttribute('aria-selected', i === start ? 'true' : 'false');
      });
    }
    function go(delta) {
      start = (start + delta + n) % n;
      apply(true);
    }
    apply(false);

    if (n <= 3) return;
    if (prev && !prev.__mkBound) { prev.__mkBound = true; prev.addEventListener('click', function () { go(-1); }); }
    if (next && !next.__mkBound) { next.__mkBound = true; next.addEventListener('click', function () { go(1); }); }
    dots.forEach(function (d, i) {
      if (d.__mkBound) return;
      d.__mkBound = true;
      d.addEventListener('click', function () { start = i; apply(true); });
    });
    grid.__mkGo = go;
    grid.__mkSet = function (i) { start = i; apply(true); };

    if (!autoplay) return;
    if (!grid.__mkPauseBound) {
      grid.__mkPauseBound = true;
      /* Pause while a reader is over or inside the cards (WCAG 2.2.2). In the Theme Editor the
         pointer is almost always over the preview, so hover never pauses there. */
      grid.addEventListener('mouseenter', function () { if (!designMode) grid.__mkPaused = true; });
      grid.addEventListener('mouseleave', function () { grid.__mkPaused = false; });
      grid.addEventListener('focusin', function () { grid.__mkPaused = true; });
      grid.addEventListener('focusout', function () { grid.__mkPaused = false; });
    }
    grid.__mkPaused = false;
    timers.set(grid, window.setInterval(function () {
      if (grid.__mkPaused || document.hidden) return;
      go(1);
    }, interval));
  }

  /* ---------- horizontal tracks (dinner ideas with more than three cards) ---------- */
  function initCarousel(track) {
    var section = track.closest('.mk-section') || track.parentElement;
    var prev = section.querySelector('[data-mk-carousel-prev]');
    var next = section.querySelector('[data-mk-carousel-next]');
    var count = section.querySelector('[data-mk-carousel-count]');
    var cards = Array.prototype.slice.call(track.children);
    if (!cards.length) return;

    function step() {
      var first = cards[0];
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 16;
      return first.getBoundingClientRect().width + gap;
    }
    function update() {
      var max = track.scrollWidth - track.clientWidth - 1;
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= max;
      if (count) {
        var per = Math.max(1, Math.round((track.clientWidth + 16) / step()));
        var firstIndex = Math.round(track.scrollLeft / step());
        var from = Math.min(cards.length, firstIndex + 1);
        var to = Math.min(cards.length, firstIndex + per);
        count.textContent = (from === to ? from : from + '\u2013' + to) + ' / ' + cards.length;
      }
    }
    if (!track.__mkBound) {
      track.__mkBound = true;
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }); });
      track.addEventListener('scroll', function () {
        window.cancelAnimationFrame(track.__mkRaf || 0);
        track.__mkRaf = window.requestAnimationFrame(update);
      }, { passive: true });
      window.addEventListener('resize', update);
    }
    update();
  }

  /* ---------- Build your menu: frequency (selling plans) ---------- */
  function initFrequency(fieldset) {
    var section = fieldset.closest('.mk-section') || document;
    var radios = Array.prototype.slice.call(fieldset.querySelectorAll('input[type="radio"]'));
    var cards = Array.prototype.slice.call(section.querySelectorAll('[data-mk-menu-card]'));

    function apply() {
      var checked = radios.filter(function (r) { return r.checked; })[0];
      var planId = checked ? checked.value : '';
      cards.forEach(function (card) {
        var input = card.querySelector('[data-mk-selling-plan]');
        var price = card.querySelector('[data-mk-price]');
        var plans = {};
        try { plans = JSON.parse(card.getAttribute('data-mk-plans') || '{}'); } catch (e) { plans = {}; }
        var plan = planId ? plans[planId] : null;
        if (input) {
          if (plan) { input.value = planId; input.disabled = false; } else { input.value = ''; input.disabled = true; }
        }
        if (price) {
          var base = price.getAttribute('data-base') || price.textContent;
          if (plan && plan.price && plan.price !== base) {
            price.innerHTML = '';
            price.appendChild(document.createTextNode(plan.price));
            var del = document.createElement('del');
            del.textContent = plan.compare || base;
            price.appendChild(del);
          } else {
            price.textContent = base;
          }
        }
        card.classList.toggle('mk-menu-card--one-time-only', !!planId && !plan);
      });
    }
    if (!fieldset.__mkBound) {
      fieldset.__mkBound = true;
      fieldset.addEventListener('change', apply);
    }
    apply();
  }

  /* ---------- order minimum: soft gate on checkout buttons ---------- */
  var lastCount = typeof MK.itemCount === 'number' ? MK.itemCount : null;
  function applyCartGate(count) {
    if (!MK.cartGate || !(MK.minCount > 0) || typeof count !== 'number') return;
    var buttons = document.querySelectorAll('#CartDrawer-Checkout, #checkout, .cart__checkout-button');
    var under = count > 0 && count < MK.minCount;
    var labels = MK.menuLabels || {};
    Array.prototype.forEach.call(buttons, function (btn) {
      /* Taste wraps the button in a flex row (.cart__ctas); the note goes above that row, full width. */
      var anchor = btn.closest('.cart__ctas') || btn;
      var parent = anchor.parentElement;
      var note = parent ? parent.querySelector('.mk-cart-gate') : null;
      if (under) {
        if (!note && parent) {
          note = document.createElement('p');
          note.className = 'mk-cart-gate';
          note.setAttribute('role', 'status');
          note.innerHTML = '<span class="mk-cart-gate__text"></span>';
          if (MK.buildUrl) {
            var back = document.createElement('a');
            back.href = MK.buildUrl + '#menu';
            back.setAttribute('data-mk-keep', '');
            back.textContent = labels.back || 'Back to your menu';
            note.appendChild(document.createTextNode(' '));
            note.appendChild(back);
          }
          parent.insertBefore(note, anchor);
        }
        var text = note ? note.querySelector('.mk-cart-gate__text') || note : null;
        if (text) text.textContent = fill(MK.minMessage, { remaining: MK.minCount - count, min: MK.minCount, free: MK.freeCount || 0 });
        btn.setAttribute('data-mk-gated', '1');
        btn.setAttribute('aria-disabled', 'true');
        btn.disabled = true;
      } else if (btn.getAttribute('data-mk-gated') === '1') {
        btn.removeAttribute('data-mk-gated');
        btn.removeAttribute('aria-disabled');
        btn.disabled = count === 0; /* Taste keeps the button disabled while the cart is empty */
        if (note && note.parentNode) note.parentNode.removeChild(note);
      }
    });
    /* Express buttons (Shop Pay, PayPal…) on /cart skip the theme's checkout button; hide them under the minimum. */
    Array.prototype.forEach.call(document.querySelectorAll('.cart__dynamic-checkout-buttons, .additional-checkout-buttons'), function (el) {
      if (under) { el.setAttribute('data-mk-gated', '1'); el.hidden = true; }
      else if (el.getAttribute('data-mk-gated') === '1') { el.removeAttribute('data-mk-gated'); el.hidden = false; }
    });
  }

  /* ---------- contact form: topic pre-select (?topic=…) + per-topic hint ---------- */
  function initContact(form) {
    var select = form.querySelector('[data-mk-topic]');
    var hint = form.querySelector('[data-mk-topic-hint]');
    if (!select) return;
    function syncHint() {
      if (!hint) return;
      var opt = select.options[select.selectedIndex];
      var key = opt ? (opt.getAttribute('data-key') || '') : '';
      var match = hint.getAttribute('data-match') || '';
      hint.hidden = !(match && key === match);
    }
    if (!form.__mkBound) {
      form.__mkBound = true;
      var wanted = '';
      try { wanted = (new URLSearchParams(window.location.search).get('topic') || '').toLowerCase(); } catch (e) { wanted = ''; }
      if (wanted) {
        for (var i = 0; i < select.options.length; i++) {
          var key = (select.options[i].getAttribute('data-key') || '').toLowerCase();
          if (key === wanted || key.indexOf(wanted) !== -1) { select.selectedIndex = i; break; }
        }
      }
      select.addEventListener('change', syncHint);
    }
    syncHint();
  }

  /* ---------- FAQ accordions ---------- */
  function initFaq(root) {
    if (root.__mkBound) return;
    root.__mkBound = true;
    root.addEventListener('toggle', function (event) {
      var item = event.target;
      if (!item.matches || !item.matches('details.mk-faq-item') || !item.open) return;
      if (root.hasAttribute('data-single-open')) {
        var list = item.closest('.mk-faq-list') || root;
        Array.prototype.forEach.call(list.querySelectorAll('details.mk-faq-item[open]'), function (other) {
          if (other !== item) other.open = false;
        });
      }
    }, true);
    /* Deep links: /pages/contact#faq-shipping scrolls to the group; a hash matching a question id opens it. */
    if (window.location.hash) {
      var target = document.getElementById(window.location.hash.slice(1));
      if (target && target.matches && target.matches('details.mk-faq-item')) target.open = true;
    }
  }

  /* ---------- catalog links → Build-your-menu page ---------- */
  /* MK.redirectProducts === false keeps product links on the product page (which now sends shoppers to the Build page itself). */
  var catalogRe = /^\/(products|collections)(\/|$|\?|#)/;
  var collectionRe = /^\/collections(\/|$|\?|#)/;
  function redirectCatalogLinks(scope) {
    if (!MK.redirectCatalogLinks || !MK.buildUrl || designMode) return;
    var re = MK.redirectProducts === false ? collectionRe : catalogRe;
    var root = scope && scope.querySelectorAll ? scope : document;
    var links = Array.prototype.slice.call(root.querySelectorAll('a[href]'));
    if (root !== document && root.matches && root.matches('a[href]')) links.push(root);
    links.forEach(function (a) {
      if (a.hasAttribute('data-mk-keep') || a.closest('[data-mk-keep]')) return;
      var href = a.getAttribute('href') || '';
      var path = href;
      if (/^https?:\/\//i.test(href)) {
        try {
          var u = new URL(href);
          if (u.host !== window.location.host) return;
          path = u.pathname + u.search + u.hash;
        } catch (e) { return; }
      }
      if (!re.test(path) || /^\/collections\/[^/]+\/products\//.test(path) && MK.redirectProducts === false) return;
      a.setAttribute('href', MK.buildUrl);
      a.setAttribute('data-mk-redirected', path);
    });
  }

  /* ---------- quantity steppers ---------- */
  function initQty(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('[data-mk-qty]'), function (btn) {
      if (btn.__mkBound) return;
      btn.__mkBound = true;
      btn.addEventListener('click', function () {
        var input = btn.parentElement.querySelector('input');
        if (!input || input.disabled) return;
        var step = parseInt(btn.getAttribute('data-mk-qty'), 10) || 1;
        var min = parseInt(input.min, 10) || 1;
        var max = parseInt(input.max, 10) || 99;
        var val = (parseInt(input.value, 10) || min) + step;
        input.value = Math.min(max, Math.max(min, val));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* ---------- live menu summary (Build your menu page) ---------- */
  function fill(template, data) {
    return String(template || '').replace(/\[(remaining|min|free|threshold)\]/g, function (_, k) { return k === 'threshold' ? data.free : data[k]; });
  }
  function renderSummary(el, count) {
    var min = parseInt(el.getAttribute('data-min'), 10) || 0;
    var free = parseInt(el.getAttribute('data-free'), 10) || 0;
    var countEl = el.querySelector('[data-mk-count]');
    var unitEl = el.querySelector('[data-mk-unit]');
    var meter = el.querySelector('[data-mk-meter]');
    var text = el.querySelector('[data-mk-summary-text]');
    if (countEl) countEl.textContent = count;
    if (unitEl) unitEl.textContent = count === 1 ? unitEl.getAttribute('data-one') : unitEl.getAttribute('data-many');
    var target = free > 0 ? free : min;
    var pct = target > 0 ? Math.min(100, Math.round((count / target) * 100)) : 0;
    if (meter) meter.style.width = pct + '%';
    if (text) {
      var msg;
      if (min > 0 && count < min) {
        msg = fill(el.getAttribute('data-text-under'), { remaining: min - count, min: min, free: free });
      } else if (free > 0 && count < free) {
        msg = fill(el.getAttribute('data-text-met'), { remaining: free - count, min: min, free: free });
      } else {
        msg = fill(el.getAttribute('data-text-free'), { remaining: 0, min: min, free: free });
      }
      text.textContent = msg;
    }
    var cta = el.querySelector('[data-mk-summary-cta]');
    if (cta) {
      var gate = min > 0 && count < min;
      if (gate) { cta.setAttribute('aria-disabled', 'true'); cta.setAttribute('tabindex', '-1'); }
      else { cta.removeAttribute('aria-disabled'); cta.removeAttribute('tabindex'); }
    }
    el.setAttribute('data-count', count);
  }
  function applyCount(count) {
    lastCount = count;
    Array.prototype.forEach.call(document.querySelectorAll('[data-mk-menu-summary]'), function (el) { renderSummary(el, count); });
    applyCartGate(count);
  }
  function refreshCart() {
    var url = (window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js';
    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { applyCount(cart.item_count || 0); })
      .catch(function () {});
  }
  var cartSubscribed = false;
  function initCart(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('[data-mk-menu-summary]'), function (el) {
      renderSummary(el, parseInt(el.getAttribute('data-count'), 10) || 0);
    });
    if (typeof lastCount === 'number') applyCartGate(lastCount);
    var needed = document.querySelector('[data-mk-menu-summary]') || (MK.cartGate && MK.minCount > 0);
    if (!cartSubscribed && needed) {
      cartSubscribed = true;
      var tries = 0;
      (function bind() {
        /* Taste declares PUB_SUB_EVENTS with a top-level const, so it is a global binding but not a window property. */
        var events = null;
        try { events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS; } catch (e) { events = window.PUB_SUB_EVENTS; }
        if (typeof window.subscribe === 'function' && events && events.cartUpdate) {
          window.subscribe(events.cartUpdate, function () { refreshCart(); });
        } else if (tries++ < 20) {
          window.setTimeout(bind, 150);
        }
      })();
      document.addEventListener('visibilitychange', function () { if (!document.hidden) refreshCart(); });
    }
  }

  /* ---------- init ---------- */
  function initCompare(root) {
    if (root.__mkCompare) return;
    root.__mkCompare = true;
    var chips = Array.prototype.slice.call(root.querySelectorAll('[data-mk-compare-chip]'));
    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        root.setAttribute('data-active', chip.getAttribute('data-mk-compare-chip'));
        chips.forEach(function (c) { c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'); });
      });
    });
  }

  function initAll(scope) {
    scope = scope || document;
    document.documentElement.classList.toggle('mk-motion', !reduceMotion.matches);
    syncHeaderHeight();
    Array.prototype.forEach.call(scope.querySelectorAll('[data-mk-rotate]'), initRotator);
    Array.prototype.forEach.call(scope.querySelectorAll('.mk-hero'), initHeroVideo);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-mk-reviews]'), initReviews);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-mk-carousel]'), initCarousel);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-mk-frequency]'), initFrequency);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-mk-contact]'), initContact);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-mk-faq]'), initFaq);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-mk-compare]'), initCompare);
    initQty(scope);
    initCart(scope);
    redirectCatalogLinks(scope);
    initMobileCta();
  }

  /* Re-apply to markup that arrives later (cart drawer re-renders, predictive search, app blocks). */
  var observerQueued = false;
  function observeLateMarkup() {
    if (!('MutationObserver' in window) || !document.body) return;
    new MutationObserver(function (mutations) {
      var added = [];
      mutations.forEach(function (m) {
        Array.prototype.forEach.call(m.addedNodes, function (n) { if (n.nodeType === 1) added.push(n); });
      });
      if (!added.length) return;
      added.forEach(redirectCatalogLinks);
      if (observerQueued) return;
      observerQueued = true;
      window.setTimeout(function () {
        observerQueued = false;
        if (typeof lastCount === 'number') applyCartGate(lastCount);
      }, 60);
    }).observe(document.body, { childList: true, subtree: true });
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(function () { initAll(document); observeLateMarkup(); });
  window.addEventListener('load', syncHeaderHeight);
  window.addEventListener('resize', syncHeaderHeight);
  if ('ResizeObserver' in window) {
    ready(function () {
      var header = document.querySelector('.mk-header');
      if (header) new ResizeObserver(syncHeaderHeight).observe(header);
    });
  }
  if (typeof reduceMotion.addEventListener === 'function') {
    reduceMotion.addEventListener('change', function () { initAll(document); });
  }

  /* Theme Editor hooks */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
    syncHeaderHeight();
  });
  document.addEventListener('shopify:section:unload', function (event) {
    Array.prototype.forEach.call(event.target.querySelectorAll('[data-mk-rotate], [data-mk-reviews]'), clearTimer);
    initMobileCta();
  });
  document.addEventListener('shopify:section:reorder', syncHeaderHeight);
  document.addEventListener('shopify:block:select', function (event) {
    var faqItem = event.target.closest('details.mk-faq-item');
    if (faqItem) { faqItem.open = true; return; }
    var review = event.target.closest('[data-mk-review]');
    if (review) {
      var grid = review.closest('[data-mk-reviews]');
      if (grid) {
        clearTimer(grid);
        var all = Array.prototype.slice.call(grid.querySelectorAll('[data-mk-review]'));
        if (grid.__mkSet) grid.__mkSet(all.indexOf(review));
      }
      return;
    }
    var item = event.target.closest('[data-mk-rotate-item]');
    if (!item) return;
    var root = item.closest('[data-mk-rotate]');
    if (!root) return;
    clearTimer(root);
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-mk-rotate-item]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-mk-rotate-dot]'));
    var i = items.indexOf(item);
    items.forEach(function (el, n) { el.classList.toggle('is-active', n === i); });
    dots.forEach(function (el, n) { el.classList.toggle('is-active', n === i); });
  });
  document.addEventListener('shopify:block:deselect', function (event) {
    var root = event.target.closest('[data-mk-rotate]');
    if (root) initRotator(root);
    var grid = event.target.closest('[data-mk-reviews]');
    if (grid) initReviews(grid);
  });
})();
